import React, { useState, useEffect } from 'react';
import { apiKeysAPI, anthropicKeysAPI, openaiKeysAPI, customersAPI } from '../services/api';
import { format } from 'date-fns';

const PROVIDERS = {
  google: {
    name: 'Google',
    label: 'Google AI (Gemini)',
    api: apiKeysAPI,
    keyPrefix: 'AIza...',
    color: '#4285F4',
    icon: '🔷'
  },
  anthropic: {
    name: 'Anthropic',
    label: 'Anthropic (Claude)',
    api: anthropicKeysAPI,
    keyPrefix: 'sk-ant-...',
    color: '#D97757',
    icon: '🟠'
  },
  openai: {
    name: 'OpenAI',
    label: 'OpenAI (GPT)',
    api: openaiKeysAPI,
    keyPrefix: 'sk-proj-...',
    color: '#10A37F',
    icon: '🟢'
  }
};

const APIKeys = () => {
  const [activeProvider, setActiveProvider] = useState('google');
  const [apiKeys, setApiKeys] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [validating, setValidating] = useState(null);
  const [filters, setFilters] = useState({
    assignment_type: '',
    is_active: ''
  });

  const [formData, setFormData] = useState({
    name: '',
    api_key: '',
    assignment_type: 'shared',
    customer: '',
    is_active: true
  });

  const provider = PROVIDERS[activeProvider];

  useEffect(() => {
    fetchData();
  }, [filters, activeProvider]);

  useEffect(() => {
    // Fetch customers once
    customersAPI.list({ page_size: 1000 }).then(res => {
      setCustomers(res.data.results || res.data);
    }).catch(console.error);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setApiKeys([]);
    setStats(null);
    try {
      const params = {};
      if (filters.assignment_type) params.assignment_type = filters.assignment_type;
      if (filters.is_active) params.is_active = filters.is_active;

      const [keysRes, statsRes] = await Promise.all([
        provider.api.list(params),
        provider.api.stats()
      ]);

      setApiKeys(keysRes.data.results || keysRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setApiKeys([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: formData.name,
        api_key: formData.api_key,
        assignment_type: formData.assignment_type,
        is_active: formData.is_active
      };

      if (formData.assignment_type === 'customer' && formData.customer) {
        data.customer = formData.customer;
      }

      if (editingKey) {
        await provider.api.update(editingKey.id, data);
      } else {
        await provider.api.create(data);
      }

      setShowModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      console.error('Failed to save API key:', err);
      alert(err.response?.data?.detail || 'Failed to save API key');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) return;
    try {
      await provider.api.delete(id);
      fetchData();
    } catch (err) {
      console.error('Failed to delete:', err);
      alert('Failed to delete API key');
    }
  };

  const handleValidate = async (key) => {
    setValidating(key.id);
    try {
      const res = await provider.api.validate(key.id);
      alert(res.data.valid ? 'API key is valid!' : `Invalid: ${res.data.error}`);
      fetchData();
    } catch (err) {
      alert(`Validation failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setValidating(null);
    }
  };

  const handleClearCooldown = async (key) => {
    try {
      await provider.api.clearCooldown(key.id);
      fetchData();
    } catch (err) {
      console.error('Failed to clear cooldown:', err);
    }
  };

  const openEditModal = (key) => {
    setEditingKey(key);
    setFormData({
      name: key.name,
      api_key: '', // Don't show existing key
      assignment_type: key.assignment_type,
      customer: key.customer || '',
      is_active: key.is_active
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingKey(null);
    setFormData({
      name: '',
      api_key: '',
      assignment_type: 'shared',
      customer: '',
      is_active: true
    });
  };

  const getStatusBadge = (key) => {
    if (!key.is_active) return <span className="badge badge-gray">Inactive</span>;
    if (key.is_on_cooldown) return <span className="badge badge-warning">Cooldown</span>;
    if (!key.is_valid) return <span className="badge badge-danger">Invalid</span>;
    return <span className="badge badge-success">Active</span>;
  };

  const getAssignmentBadge = (type) => {
    switch (type) {
      case 'shared': return <span className="badge badge-info">Shared Pool</span>;
      case 'customer': return <span className="badge badge-purple">Customer</span>;
      case 'license': return <span className="badge badge-gray">License</span>;
      default: return <span className="badge">{type}</span>;
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">API Keys</h1>
          <p className="page-description">Manage AI API keys for embedded mode</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => { resetForm(); setShowModal(true); }}
        >
          Add {provider.name} Key
        </button>
      </div>

      {/* Provider Tabs */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '1.5rem', borderBottom: '2px solid #e5e7eb' }}>
        {Object.entries(PROVIDERS).map(([key, prov]) => (
          <button
            key={key}
            onClick={() => setActiveProvider(key)}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              background: activeProvider === key ? '#fff' : 'transparent',
              borderBottom: activeProvider === key ? `3px solid ${prov.color}` : '3px solid transparent',
              marginBottom: '-2px',
              cursor: 'pointer',
              fontWeight: activeProvider === key ? '600' : '400',
              color: activeProvider === key ? prov.color : '#6b7280',
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease'
            }}
          >
            <span>{prov.icon}</span>
            {prov.name}
          </button>
        ))}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div className="card" style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: provider.color }}>{stats.total}</div>
            <div style={{ color: 'var(--text-secondary)' }}>Total Keys</div>
          </div>
          <div className="card" style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--success)' }}>{stats.active}</div>
            <div style={{ color: 'var(--text-secondary)' }}>Active</div>
          </div>
          <div className="card" style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--warning)' }}>{stats.on_cooldown}</div>
            <div style={{ color: 'var(--text-secondary)' }}>On Cooldown</div>
          </div>
          <div className="card" style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--info)' }}>{stats.by_assignment?.shared || 0}</div>
            <div style={{ color: 'var(--text-secondary)' }}>Shared Pool</div>
          </div>
        </div>
      )}

      <div className="card">
        {/* Filters */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <select
            className="form-select"
            style={{ width: 'auto' }}
            value={filters.assignment_type}
            onChange={(e) => setFilters({ ...filters, assignment_type: e.target.value })}
          >
            <option value="">All Assignments</option>
            <option value="shared">Shared Pool</option>
            <option value="customer">Customer Specific</option>
            <option value="license">License Specific</option>
          </select>

          <select
            className="form-select"
            style={{ width: 'auto' }}
            value={filters.is_active}
            onChange={(e) => setFilters({ ...filters, is_active: e.target.value })}
          >
            <option value="">All Status</option>
            <option value="true">Active Only</option>
            <option value="false">Inactive Only</option>
          </select>
        </div>

        {loading ? (
          <div className="loading-container"><div className="spinner"></div></div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Key</th>
                  <th>Assignment</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Last Validated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                      No {provider.name} API keys found. Add your first key to enable embedded AI mode.
                    </td>
                  </tr>
                ) : (
                  apiKeys.map((key) => (
                    <tr key={key.id}>
                      <td className="font-medium">{key.name}</td>
                      <td><code style={{ fontSize: '0.85rem' }}>{key.masked_key}</code></td>
                      <td>{getAssignmentBadge(key.assignment_type)}</td>
                      <td>{key.customer_name || '-'}</td>
                      <td>{getStatusBadge(key)}</td>
                      <td>
                        {key.last_validated_at
                          ? format(new Date(key.last_validated_at), 'MMM d, HH:mm')
                          : 'Never'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleValidate(key)}
                            disabled={validating === key.id}
                          >
                            {validating === key.id ? '...' : 'Test'}
                          </button>
                          {key.is_on_cooldown && (
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => handleClearCooldown(key)}
                            >
                              Clear CD
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => openEditModal(key)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(key.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingKey ? `Edit ${provider.name} Key` : `Add ${provider.name} Key`}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Key Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Production Key 1"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    API Key {editingKey ? '(leave blank to keep existing)' : '*'}
                  </label>
                  <input
                    type="password"
                    className="form-input"
                    value={formData.api_key}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                    placeholder={provider.keyPrefix}
                    required={!editingKey}
                  />
                  <small style={{ color: 'var(--text-secondary)' }}>
                    {provider.label} API key (starts with {provider.keyPrefix})
                  </small>
                </div>

                <div className="form-group">
                  <label className="form-label">Assignment</label>
                  <select
                    className="form-select"
                    value={formData.assignment_type}
                    onChange={(e) => setFormData({ ...formData, assignment_type: e.target.value })}
                  >
                    <option value="shared">Shared Pool (all customers)</option>
                    <option value="customer">Customer Specific</option>
                  </select>
                </div>

                {formData.assignment_type === 'customer' && (
                  <div className="form-group">
                    <label className="form-label">Customer *</label>
                    <select
                      className="form-select"
                      value={formData.customer}
                      onChange={(e) => setFormData({ ...formData, customer: e.target.value })}
                      required
                    >
                      <option value="">Select Customer</option>
                      {customers.map((c) => (
                        <option key={c.id} value={c.id}>{c.company_name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    Active (available for use)
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingKey ? 'Update' : 'Add Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          backdrop-filter: blur(4px);
        }
        .modal {
          background: #ffffff;
          border-radius: 12px;
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          position: relative;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
          background: #ffffff;
        }
        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6b7280;
          padding: 0.25rem;
          line-height: 1;
        }
        .modal-close:hover {
          color: #111827;
        }
        .modal-body {
          padding: 1.5rem;
          background: #ffffff;
        }
        .modal-body .form-group {
          margin-bottom: 1.25rem;
        }
        .modal-body .form-label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: #374151;
        }
        .modal-body .form-input,
        .modal-body .form-select {
          width: 100%;
          padding: 0.625rem 0.875rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.95rem;
          background: #ffffff;
          color: #111827;
        }
        .modal-body .form-input:focus,
        .modal-body .form-select:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }
        .modal-body small {
          display: block;
          margin-top: 0.375rem;
          color: #6b7280;
          font-size: 0.85rem;
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
        }
        .badge-purple {
          background: #8b5cf6;
          color: white;
        }
        .badge-info {
          background: #0ea5e9;
          color: white;
        }
      `}</style>
    </div>
  );
};

export default APIKeys;
